import { Injectable } from '@angular/core';
import { StorageService } from '../storageService/storage.service';
import { TableColumn } from '@nagel-types';

interface ColumnState {
    columnDef: string;
    visibility: boolean;
}

interface TableState {
    columnStates: ColumnState[];
    version?: string;
}

function isTableState(obj: TableState) {
    return (
        obj &&
        Array.isArray(obj.columnStates) &&
        obj.columnStates.every(
            (col: ColumnState) =>
                typeof col.columnDef === 'string' && typeof col.visibility === 'boolean'
        )
    );
}

@Injectable({
    providedIn: 'root'
})
export class TableColumnControlService {
    private readonly LOCAL_STORAGE_KEY = 'calTableState';
    private columns!: TableColumn[];
    private tableState: TableState;

    constructor(private storage: StorageService) {
        this.tableState = this.getTableStateFromStorage();
    }

    initializeColumns(columns: TableColumn[], version: string) {
        this.columns = columns;

        const { version: storedVersion = '1' } = this.tableState;

        if (storedVersion !== version || this.tableState.columnStates.length === 0) {
            this.tableState = {
                columnStates: this.columns.map((col) => ({
                    columnDef: col.columnDef,
                    visibility: col.visible
                })),
                version
            };

            this.saveTableState();
        } else {
            this.applySavedVisibilityToColumns();
        }
    }

    saveTableState() {
        this.storage.saveItem(
            this.LOCAL_STORAGE_KEY,
            JSON.stringify(this.tableState)
        );
    }

    applySavedVisibilityToColumns() {
        const columnMap = new Map(this.columns.map((col) => [col.columnDef, col]));
        this.tableState.columnStates.forEach((colState) => {
            const column = columnMap.get(colState.columnDef);

            if (column) {
                column.visible = colState.visibility;
            }
        });
    }

    private getTableStateFromStorage(): TableState {
        let parsedState = { columnStates: [] };
        const storedState = this.storage.getItem(this.LOCAL_STORAGE_KEY);
        if (storedState) {
            parsedState = JSON.parse(storedState);
        }
        return storedState && isTableState(parsedState)
            ? parsedState
            : { columnStates: [] };
    }

    getTableState() {
        return this.tableState;
    }

    buildColumnsFromState(): TableColumn[] {
        const columnMap = new Map(this.columns.map((col) => [col.columnDef, col]));

        return this.tableState.columnStates
            .map((colState) => {
                const column = columnMap.get(colState.columnDef);

                if (column) {
                    return {
                        ...column,
                        visible: colState.visibility
                    };
                }

                return null;
            })
            .filter((col): col is TableColumn => col !== null);
    }

    getDisplayedColumns() {
        return this.buildColumnsFromState().filter((col) => col.visible);
    }

    toggleColumnVisibility(field: string): void {
        const column = this.tableState.columnStates.find(
            (col) => col.columnDef === field
        );

        if (column) {
            column.visibility = !column.visibility;
            this.saveTableState();
        }
    }

    reorderColumns(reorderedFields: string[]): void {
        const reorderedColumnStates = reorderedFields.reduce((acc, field) => {
            const colState = this.tableState.columnStates.find(
                (state) => state.columnDef === field
            );

            if (colState) {
                acc.push(colState);
            }

            return acc;
        }, [] as ColumnState[]);

        const finalColumnStates = this.tableState.columnStates.reduce(
            (acc, colState, index) => {
                if (!colState.visibility && !acc.includes(colState)) {
                    acc.splice(index, 0, colState);
                }

                return acc;
            },
            reorderedColumnStates
        );

        this.tableState.columnStates = finalColumnStates;
        this.saveTableState();
    }
}